import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { AlertTriangle } from 'lucide-react';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { ChartCard, EmptyChart } from '../charts/ChartPrimitives';
import { CHART, formatUnits } from '../../lib/reporting-format';

type AgingBucket = { bucket: string; units: number; value: number; skuCount: number };
type AgedSku = {
  skuId: string;
  sku: string;
  category: string;
  productType: string;
  units: number;
  value: number;
  ageDays: number;
  lastMovement: string;
};

export type InventoryAging = {
  buckets: AgingBucket[];
  valueAtRisk: number;
  agedSkus: AgedSku[];
};

interface InventoryAgingCardProps {
  loading: boolean;
  data: InventoryAging | undefined;
}

// Canonical bucket order; older buckets shade darker (slate → red for 180+).
const BUCKET_ORDER = ['0-30', '31-60', '61-90', '91-180', '180+', 'unknown'] as const;
const BUCKET_FILL: Record<string, string> = {
  '0-30': '#cbd5e1',
  '31-60': '#94a3b8',
  '61-90': '#64748b',
  '91-180': CHART.amber,
  '180+': CHART.red,
  unknown: '#e2e8f0',
};

/**
 * Aging: a single stacked horizontal bar of $ value per age bucket, a value at
 * risk (91+ days) callout, and a drill table of the aged SKUs. Age = days since
 * last movement; we flag, never auto-write-off (a hybrid blank idle in PAP mode
 * is not truly dead — this is a watch-list, not an accounting entry).
 */
export const InventoryAgingCard: React.FC<InventoryAgingCardProps> = ({ loading, data }) => {
  const { t } = useTranslation('inventory');
  const { format, currency } = useCurrencyFormatter();

  const orderedBuckets = React.useMemo(() => {
    if (!data) return [];
    const byKey = new Map(data.buckets.map((b) => [b.bucket, b]));
    return BUCKET_ORDER.map((key) => byKey.get(key)).filter(
      (b): b is AgingBucket => Boolean(b) && (b as AgingBucket).value > 0,
    );
  }, [data]);

  // Single stacked row: one datum, each bucket a series, so the bar reads as one
  // composite bar of $ value across age bands.
  const stackedRow = React.useMemo(() => {
    const row: Record<string, number | string> = { name: 'value' };
    orderedBuckets.forEach((b) => {
      row[b.bucket] = b.value;
    });
    return [row];
  }, [orderedBuckets]);

  const agedSorted = React.useMemo(
    () => (data ? [...data.agedSkus].sort((a, b) => b.ageDays - a.ageDays).slice(0, 25) : []),
    [data],
  );

  return (
    <ChartCard
      title={t('reporting.v2.aging', 'Inventory aging')}
      description={t('reporting.v2.agingDesc', 'Days since last movement. A watch-list, not a write-off.')}
      action={
        data && data.valueAtRisk > 0 ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-300/80">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            {t('reporting.v2.atRiskCallout', '{{v}} at risk (91+ days)', {
              v: format(data.valueAtRisk, currency),
            })}
          </span>
        ) : undefined
      }
    >
      {loading ? (
        <div className="h-80 animate-pulse rounded bg-muted/40" />
      ) : !data || orderedBuckets.length === 0 ? (
        <EmptyChart message={t('reporting.v2.noAging', 'No aged stock to report.')} className="h-80" />
      ) : (
        <div className="space-y-4">
          {/* Stacked value-by-bucket bar */}
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stackedRow} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: CHART.axis }}
                  tickLine={false}
                  axisLine={{ stroke: CHART.grid }}
                  tickFormatter={(v: number) => format(v, currency)}
                />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip
                  formatter={(v: number, name: string) => [format(v, currency), name]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                {orderedBuckets.map((b) => (
                  <Bar
                    key={b.bucket}
                    dataKey={b.bucket}
                    stackId="age"
                    fill={BUCKET_FILL[b.bucket] ?? CHART.slate}
                    maxBarSize={48}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Bucket legend with $ + sku count */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
            {orderedBuckets.map((b) => (
              <div key={b.bucket} className="flex items-center gap-2 text-xs">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: BUCKET_FILL[b.bucket] ?? CHART.slate }}
                />
                <span className="text-muted-foreground">{b.bucket}d</span>
                <span className="ml-auto font-medium tabular-nums">{format(b.value, currency)}</span>
              </div>
            ))}
          </div>

          {/* Drill table of aged SKUs */}
          {agedSorted.length > 0 && (
            <div className="max-h-72 overflow-auto rounded-md border">
              <table className="w-full min-w-[30rem] border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-card">
                  <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                    <th className="py-2 pl-3 pr-3 font-medium">{t('reporting.v2.sku', 'SKU')}</th>
                    <th className="py-2 px-3 text-right font-medium">{t('reporting.v2.units', 'Units')}</th>
                    <th className="py-2 px-3 text-right font-medium">{t('reporting.v2.value', 'Value')}</th>
                    <th className="py-2 pl-3 pr-3 text-right font-medium">{t('reporting.v2.age', 'Age')}</th>
                  </tr>
                </thead>
                <tbody>
                  {agedSorted.map((row) => (
                    <tr key={`${row.skuId}-${row.sku}`} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pl-3 pr-3">
                        <p className="font-mono text-xs font-medium">{row.sku}</p>
                        <p className="text-xs text-muted-foreground">
                          {row.category} · {row.productType}
                        </p>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">{formatUnits(row.units)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{format(row.value, currency)}</td>
                      <td className="py-2 pl-3 pr-3 text-right tabular-nums text-muted-foreground">
                        {t('reporting.v2.ageDays', '{{n}}d', { n: row.ageDays })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ChartCard>
  );
};
